import {
  HYDRATABLE_PRODUCT_VENDOR_ID,
  rawArrayToObject,
} from '../../../player/src/shared/utils/productCatalog';

export const CATALOG_RAW_FORMAT = 'phV4';

const toCatalogRawKey = (productId, feedId, orgId) => (
  JSON.stringify([
    feedId && feedId !== orgId ? feedId : `org:${orgId}`,
    String(productId),
  ])
);

// Build the product-catalog query for a batch of cataloged products. Actual feed products
// use productsByFeed[feedId]; org-owned products use `ids`. VOD stores orgId in feedId as
// the sentinel for an org-owned catalog product.
export const buildCatalogRawSearchParams = (products, orgId) => {
  const params = new URLSearchParams({ orgId, format: CATALOG_RAW_FORMAT });
  const productsByFeed = Object.create(null);
  const orgProductIds = [];
  for (const { productId, feedId } of (Array.isArray(products) ? products : [])) {
    if (!productId) continue;
    if (feedId && feedId !== orgId) {
      (productsByFeed[feedId] = productsByFeed[feedId] || []).push(productId);
    } else {
      orgProductIds.push(productId);
    }
  }
  for (const feedId of Object.keys(productsByFeed)) {
    params.append(`productsByFeed[${feedId}]`, productsByFeed[feedId].join(','));
  }
  if (orgProductIds.length) params.append('ids', orgProductIds.join(','));
  return params;
};

// Reduce phV4 catalog products to a `feed + sku -> { raw, vendor }` map. Resolves each
// product's default variation and normalizes its raw feed array to an object. Products
// with no resolvable raw are skipped. The composite key keeps identical product IDs
// from different feeds separate. Feedless products use the catalog's `org:<orgId>` key.
export const catalogProductsToRawMap = (catalogProducts, orgId) => (
  (Array.isArray(catalogProducts) ? catalogProducts : []).reduce((map, catalogProduct) => {
    const sku = catalogProduct?.product?.sku;
    if (!sku) return map;
    const variations = catalogProduct.product?.variations || [];
    const defaultVariation = variations[catalogProduct.product?.defaultVariationIndex ?? 0] || variations[0];
    const raw = rawArrayToObject(defaultVariation?.meta?.data?.raw);
    if (!raw) return map;
    return map.set(
      toCatalogRawKey(sku, catalogProduct.product?.feedId, orgId),
      { raw, vendor: HYDRATABLE_PRODUCT_VENDOR_ID },
    );
  }, new Map())
);

// Merge resolved catalog raw into preview products by feed and product ID. Only
// `raw`/`vendor` are merged — `ref` already equals the product's own
// `productReference`. Non-matching products are left untouched.
export const mergeCatalogRawIntoProducts = (products, rawById, orgId) => {
  if (!Array.isArray(products) || !rawById || rawById.size === 0) return products;
  return products.map((product) => {
    const entry = product?.productId
      && rawById.get(toCatalogRawKey(product.productId, product.feedId, orgId));
    if (!entry) return product;
    return {
      ...product,
      ...(entry.raw && { raw: entry.raw }),
      ...(entry.vendor && { vendor: entry.vendor }),
    };
  });
};
